// import { Dispatch, MutableRefObject, useEffect } from "react"

import { useMap } from "@vis.gl/react-google-maps"
import { RefObject, useEffect } from "react"
import { useAppDispatch, useAppSelector } from "src/app/hooks"
import {
  createDrawingDetail,
  drawingModeSelector,
  updateDrawingDetail,
} from "src/app/slices/dispatcherRoutePlan.slice"
import {
  DrawResult,
  isCircle,
  isPolygon,
  isRectangle,
} from "../models/RouteMap.model"

export function useDrawingManagerEvents(
  drawingManager: google.maps.drawing.DrawingManager | null,
  overlaysShouldUpdateRef: RefObject<boolean>,
) {
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!drawingManager) return

    const eventListeners: Array<google.maps.MapsEventListener> = []

    const addUpdateListener = (eventName: string, drawResult: DrawResult) => {
      const updateListener = google.maps.event.addListener(
        drawResult.overlay,
        eventName,
        () => {
          if (eventName === "dragstart") {
            overlaysShouldUpdateRef.current = false
          }

          if (eventName === "dragend") {
            overlaysShouldUpdateRef.current = true
          }

          if (overlaysShouldUpdateRef.current) {
            dispatch(updateDrawingDetail())
          }
        },
      )

      eventListeners.push(updateListener)
    }

    const overlayCompleteListener = google.maps.event.addListener(
      drawingManager,
      "overlaycomplete",
      (drawResult: DrawResult) => {
        switch (drawResult.type) {
          case google.maps.drawing.OverlayType.CIRCLE:
            ;["center_changed", "radius_changed"].forEach((eventName) =>
              addUpdateListener(eventName, drawResult),
            )
            break
          case google.maps.drawing.OverlayType.POLYGON:
            ;["mouseup"].forEach((eventName) =>
              addUpdateListener(eventName, drawResult),
            )
            break

          case google.maps.drawing.OverlayType.RECTANGLE:
            ;["bounds_changed", "dragstart", "dragend"].forEach((eventName) =>
              addUpdateListener(eventName, drawResult),
            )
            break
        }

        dispatch(
          createDrawingDetail({
            drawResult,
          }),
        )
      },
    )

    eventListeners.push(overlayCompleteListener)

    return () => {
      eventListeners.forEach((listener) => {
        google.maps.event.removeListener(listener)
      })
    }
  }, [dispatch, drawingManager, overlaysShouldUpdateRef])
}

export function useOverlaySnapshots(
  overlaysShouldUpdateRef: RefObject<boolean>,
) {
  const map = useMap()
  const state = useAppSelector((state) => state.routePlanning.drawingDetail)
  const drawingFlag = useAppSelector(drawingModeSelector)

  useEffect(() => {
    if (!map || !state.now) return

    for (const overlay of state.now) {
      overlaysShouldUpdateRef.current = false

      overlay.geometry.setEditable(drawingFlag)
      overlay.geometry.setDraggable(drawingFlag)
      overlay.geometry.setMap(map)

      const { radius, center, path, bounds } = overlay.snapshot

      if (isCircle(overlay.geometry)) {
        overlay.geometry.setRadius(radius ?? 0)
        overlay.geometry.setCenter(center ?? null)
      } else if (isPolygon(overlay.geometry)) {
        overlay.geometry.setPath(path ?? [])
      } else if (isRectangle(overlay.geometry)) {
        overlay.geometry.setBounds(bounds ?? null)
      }

      overlaysShouldUpdateRef.current = true
    }

    return () => {
      for (const overlay of state.now) {
        overlay.geometry.setMap(null)
      }
    }
  }, [drawingFlag, map, overlaysShouldUpdateRef, state.now])
}
